import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { LOADER_SIZE, LOADER_VARIANTS } from '../constants';
import { indeterminateProgressKeyFrame } from '../shared-styles';

const StyledProgressTrack = styled.div`
  background: ${({ theme }) => theme.color.progressTrack.background.value};
  border-radius: 8px;
  height: ${({ loaderSize }) => (loaderSize === LOADER_SIZE.SMALL ? '4px' : '8px')};
  max-width: 768px;
  min-width: 48px;
  overflow: hidden;
  pointer-events: none;
  position: relative;
  width: ${({ trackWidth }) => trackWidth};
  ${({ variant }) =>
    variant === LOADER_VARIANTS.INDETERMINATE &&
    css`
      ::before {
        animation: ${indeterminateProgressKeyFrame} 1.5s cubic-bezier(0.45, 0, 0.4, 1) infinite;
        background: ${({ theme }) => theme.color.primary[500].value};
        content: '';
        height: 100%;
        position: absolute;
        top: 0;
        width: 50%;
      }
    `}

  ${({ percentComplete, variant }) =>
    variant === LOADER_VARIANTS.DETERMINATE &&
    css`
      ::before {
        transition: width 0.25s cubic-bezier(0.45, 0, 0.4, 1);
        background: ${({ theme }) => theme.color.primary[500].value};
        content: '';
        height: 100%;
        position: absolute;
        top: 0;
        width: ${percentComplete}%;
      }
    `}
`;
const StyledProgressTrackContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  margin: 8px;
  max-width: 768px;
  min-width: 48px;
  overflow: hidden;
  width: ${({ trackWidth }) => trackWidth};
`;

const ProgressTrack = ({
  className,
  dataTestId = '',
  isDeterminate = true,
  isIndeterminate = false,
  loaderSize = LOADER_SIZE.LARGE,
  percentComplete = undefined,
  trackWidth,
  variant = undefined,
  ...other
}) => {
  return (
    <StyledProgressTrackContainer
      data-testid={dataTestId ? `${dataTestId}-progress-track-container` : undefined}
      {...{ isDeterminate, isIndeterminate, trackWidth }}
      {...other}
    >
      <StyledProgressTrack
        data-testid={dataTestId ? `${dataTestId}-progress-track` : undefined}
        {...{ className, loaderSize, percentComplete, trackWidth, variant }}
      />
    </StyledProgressTrackContainer>
  );
};

ProgressTrack.propTypes = {
  /** Adds additional class name to the progress track for styling */
  className: PropTypes.string,
  /** Id value used for testing */
  dataTestId: PropTypes.string,
  /** Indicates if this progress indicator is determinate */
  isDeterminate: PropTypes.bool,
  /** Indicates if this progress indicator is indeterminate */
  isIndeterminate: PropTypes.bool,
  /** Determines the size (height and font size) of indeterminate progress loader */
  loaderSize: PropTypes.string,
  /** Determines the width of the progress track fill based on percent complete */
  percentComplete: PropTypes.number,
  /** Determines the width property of the progress track */
  trackWidth: PropTypes.string.isRequired,
  /** Determines whether to display determinate or indeterminate progress track variant */
  variant: PropTypes.oneOf(Object.values(LOADER_VARIANTS)),
};

export { ProgressTrack };
